import React from "react";
import {
  Paragraph,
  Button,
  FormControl,
  Select,
  Checkbox,
  Spinner,
  Notification,
  Textarea,
} from "@contentful/f36-components";
import { SidebarAppSDK } from "@contentful/app-sdk";
import { createClient } from "contentful-management";
import { translateSingleEntry, translateMultipleEntries, pollJobUntilComplete } from "../api/backendClient";
import { updateToast, showTemporaryToast, closeToast } from "../utils/persistentToast";

interface SidebarProps {
  sdk: SidebarAppSDK;
}

const TRANSLATION_SERVICE_OPTIONS: string[] = [
  "Human Translation",
  "AI-Powered Translation + Post-Editing",
  "AI-Powered Translation"
];

// Content types for which related entries will NOT be exported recursively
const IGNORE_RELATED_ENTRIES_CONTENT_TYPES = [
  "lodge", "page", "pageGeneric", "pageHomepage", "contentArticle",
  "pages", "promotions", "skill", "productPage", "pg-page", "appcontent", "AppContent"
];

const Sidebar = (props: SidebarProps) => {
  // Gets the Contentful SDK
  const { sdk } = props;

  let systemProps = sdk.entry.getSys();
  let otherLocales: string = "";
  let [sourceLanguage, setSourceLanguage] = React.useState(sdk.locales.available[0]);
  let [targetLanguages, setTargetLanguages] = React.useState<string[]>([]);
  let [shouldHideButton, setShouldHideButton] = React.useState(false);
  let [shouldHideSpinner, setShouldHideSpinner] = React.useState(true);
  let [translationService, setTranslationService] = React.useState(TRANSLATION_SERVICE_OPTIONS[0]);
  let [progressMessage, setProgressMessage] = React.useState("Submitting request...");
  let [comments, setComments] = React.useState("");
  let [exportReferencedEntries, setExportReferencedEntries] = React.useState(true);

  const onSelectSourceLanguage = (langCode: string) => {
    setSourceLanguage(langCode);
  };

  const onSelectTargetLanguage = (checked: boolean, langCode: string) => {
    if (checked) {
      if (!targetLanguages.includes(langCode)) {
        setTargetLanguages([...targetLanguages, langCode]);
      }
    } else {
      setTargetLanguages(targetLanguages.filter((e) => e !== langCode));
    }
  };

  // ── Related-entry resolution helpers (mirrors NewRequest logic) ──────────

  const multiQueue = (max: number) => {
    max = isNaN(max) || max < 1 ? 1 : max;
    const q = new Array(max).fill(0).map(() => Promise.resolve());
    let index = 0;
    const add = (cb: any, ...args: any) => {
      index = (index + 1) % max;
      return (q[index] = q[index].then(() => cb(...args)));
    };
    return add;
  };

  const createRateLimiter = (requestsPerSecond: number) => {
    let queue: Array<() => Promise<any>> = [];
    let processing = false;
    let requestCount = 0;
    let lastResetTime = Date.now();

    const processQueue = async () => {
      if (processing || queue.length === 0) return;
      processing = true;
      while (queue.length > 0) {
        const now = Date.now();
        const timeElapsed = now - lastResetTime;
        if (timeElapsed >= 1000) { requestCount = 0; lastResetTime = now; }
        if (requestCount >= requestsPerSecond) {
          await new Promise((resolve) => setTimeout(resolve, 1000 - timeElapsed));
          requestCount = 0;
          lastResetTime = Date.now();
        }
        const request = queue.shift();
        if (request) { requestCount++; await request(); }
      }
      processing = false;
    };

    return (fn: () => Promise<any>) =>
      new Promise((resolve, reject) => {
        queue.push(async () => {
          try { resolve(await fn()); } catch (error) { reject(error); }
        });
        processQueue();
      });
  };

  const getRichtTextEmbeddedEntries = (fieldValue: any, entriesToFetch: any[]) => {
    if (fieldValue.content && Array.isArray(fieldValue.content)) {
      fieldValue.content.forEach((contentEntry: any) => {
        if (
          contentEntry.data?.target &&
          contentEntry.nodeType?.includes("embedded-entry")
        ) {
          contentEntry.data.target.embedded = true;
          entriesToFetch.push(contentEntry.data.target);
        }
        if (contentEntry.content && Array.isArray(contentEntry.content)) {
          getRichtTextEmbeddedEntries(contentEntry, entriesToFetch);
        }
      });
    }
  };

  const setEntryContentTypeFields = (entry: any, contentType: any) => {
    if (!contentType) return;
    entry.typeInternalName = contentType.sys.id;
    const localizedFields: string[] = [];
    const richTextFields: string[] = [];
    contentType.fields.forEach((field: any) => {
      if (field.localized) localizedFields.push(field.id);
      if (field.type === "RichText") richTextFields.push(field.id);
    });
    entry.localizedFields = localizedFields;
    entry.richTextFields = richTextFields;
  };

  const getRelatedEntries = (entry: any, environment: any, cts: any[], srcLang: string) => {
    const relatedEntriesIds: any[] = [];

    for (const field in entry.fields) {
      let fieldValue = entry.fields[field][srcLang];
      const entriesToFetch: any[] = [];

      if (Array.isArray(fieldValue)) {
        entriesToFetch.push(...fieldValue);
        entriesToFetch.forEach((arrEntry: any) => {
          if (arrEntry.content && Array.isArray(arrEntry.content)) {
            getRichtTextEmbeddedEntries(arrEntry, entriesToFetch);
          }
        });
      } else {
        entriesToFetch.push(fieldValue);
        if (fieldValue) getRichtTextEmbeddedEntries(fieldValue, entriesToFetch);
      }

      entriesToFetch.forEach((entryToFetch: any) => {
        if (
          !entryToFetch?.sys ||
          entryToFetch.sys.type !== "Link" ||
          entryToFetch.sys.linkType !== "Entry"
        ) return;

        const alreadyAdded = relatedEntriesIds.some((e: any) => e.id === entryToFetch.sys.id);
        if (!alreadyAdded) {
          relatedEntriesIds.push({ id: entryToFetch.sys.id, embedded: entryToFetch.embedded });
        }
      });
    }

    if (relatedEntriesIds.length === 0) return Promise.resolve([]);

    const idsList = relatedEntriesIds.map((e: any) => e.id).join(",");

    return environment.getEntries({ "sys.id[in]": idsList }).then((res: any) => {
      const relatedEntries: any[] = [];
      if (res?.items) {
        res.items.forEach((related: any) => {
          if (!relatedEntries.some((r: any) => r.sys.id === related.sys.id)) {
            const ct = cts.find((c: any) => c.sys.id === related.sys.contentType.sys.id);
            setEntryContentTypeFields(related, ct);
            relatedEntries.push(related);
          }
        });
      }
      return relatedEntries;
    });
  };

  const getRelatedEntriesRecursive = (
    entry: any,
    relatedEntries: any[],
    level: number,
    rateLimiter: any,
    environment: any,
    cts: any[],
    srcLang: string
  ): Promise<any[]> => {
    const promises: Promise<any>[] = [];

    return rateLimiter(() => getRelatedEntries(entry, environment, cts, srcLang)).then((res: any) => {
      const queue = multiQueue(6);

      res.forEach((related: any) => {
        if (relatedEntries.some((r: any) => r.sys.id === related.sys.id)) return;

        const shouldIgnore = IGNORE_RELATED_ENTRIES_CONTENT_TYPES.some(
          (t) => t === related.typeInternalName
        );
        if (shouldIgnore) return;

        relatedEntries.push(related);
        promises.push(
          queue(() => getRelatedEntriesRecursive(related, relatedEntries, level + 1, rateLimiter, environment, cts, srcLang))
        );
      });

      return Promise.all(promises).then((results) => {
        results.forEach((res) => {
          res.forEach((related: any) => {
            if (!relatedEntries.some((r: any) => r.sys.id === related.sys.id)) {
              relatedEntries.push(related);
            }
          });
        });
        return relatedEntries;
      });
    });
  };

  // ────────────────────────────────────────────────────────────────────────

  // Translated Into field value setup
  sdk.locales.available.forEach((locale, i) => {
    if (locale === sdk.locales.default) {
      return;
    }

    if (otherLocales !== "") {
      otherLocales += ", ";
    }

    otherLocales += sdk.locales.names[locale];
  });

  if (otherLocales === "") {
    otherLocales = "None";
  }

  const sendForTranslation = async () => {
    const id = systemProps.id;
    const contentType = sdk.contentType.name;

    if (!translationService) {
      Notification.setDuration(2000);
      Notification.error("Please, select a translation service.");
      return;
    }

    if (!sourceLanguage) {
      Notification.setDuration(2000);
      Notification.error("Please, select a source language.");
      return;
    }

    if (!targetLanguages || targetLanguages.length === 0) {
      Notification.setDuration(2000);
      Notification.error("Please, select at least one target language.");
      return;
    }

    if (
      targetLanguages.filter((lang) => {
        return lang === sourceLanguage;
      }).length > 0
    ) {
      Notification.setDuration(2000);
      Notification.error("The source language can not be selected as a target language.");
      return;
    }

    // Shows the spinner while submitting
    setShouldHideButton(true);
    setShouldHideSpinner(false);
    setProgressMessage("Submitting request...");

    try {
      let submitResult;

      if (exportReferencedEntries) {
        // Resolve the current entry + all related entries via CMA, then submit all IDs
        setProgressMessage("Resolving related entries...");

        const cma = createClient({ apiAdapter: sdk.cmaAdapter });
        const space = await cma.getSpace(sdk.ids.space);
        const environment = await space.getEnvironment(sdk.ids.environment);
        const contentTypesResult = await environment.getContentTypes();
        const cts = contentTypesResult.items;

        const currentEntry = await environment.getEntry(id);
        const ct = cts.find((c: any) => c.sys.id === currentEntry.sys.contentType.sys.id);
        setEntryContentTypeFields(currentEntry, ct);

        const allEntries: any[] = [currentEntry];
        const rateLimiter = createRateLimiter(50);
        await getRelatedEntriesRecursive(currentEntry, allEntries, 1, rateLimiter, environment, cts, sourceLanguage);

        const entryIds = allEntries.map((e: any) => e.sys.id);
        setProgressMessage("Submitting request...");

        submitResult = await translateMultipleEntries(
          sdk as any,
          entryIds,
          sourceLanguage,
          targetLanguages,
          translationService,
          undefined,
          comments
        );
      } else {
        // Collect localized fields for single-entry submission
        const fields: any = {};
        for (let field in sdk.entry.fields) {
          let matches = sdk.contentType.fields.filter((locField: any) => {
            return locField.localized && locField.id === field;
          });
          if (matches.length > 0) {
            fields[field] = sdk.entry.fields[field].getValue(sourceLanguage);
          }
        }

        submitResult = await translateSingleEntry(
          sdk,
          id,
          contentType,
          fields,
          sourceLanguage,
          targetLanguages,
          translationService,
          comments
        );
      }

      if (!submitResult.success || !submitResult.data?.jobId) {
        throw new Error(submitResult.error || "Failed to submit translation request");
      }

      const jobId = submitResult.data.jobId;

      // Hide spinner and re-enable button immediately so user can submit more requests
      setShouldHideButton(false);
      setShouldHideSpinner(true);
      setProgressMessage("Submitting request...");

      // Show persistent toast - same element is reused and only text changes
      updateToast("Request submitted successfully. Processing in background...", "success");

      // Poll for job completion in background (non-blocking)
      const startTime = Date.now();
      const FIVE_MINUTES = 5 * 60 * 1000;
      let hasShownLongRunningMessage = false;

      pollJobUntilComplete(jobId, (job) => {
        const elapsedTime = Date.now() - startTime;

        if (elapsedTime > FIVE_MINUTES && !hasShownLongRunningMessage) {
          hasShownLongRunningMessage = true;
          updateToast("Process is taking longer than expected. You can check the status later.", "warning");
          return;
        }

        if (job.status === 'processing' && !hasShownLongRunningMessage) {
          updateToast(`Processing translation... ${job.progress || 0}%`, "info");
        }
      }).then((completedJob) => {
        const resultCount = completedJob.result?.translatedCount || completedJob.result?.submittedCount || 0;
        const failedCount = completedJob.failedItems?.length || 0;

        if (failedCount > 0) {
          showTemporaryToast(`Translation completed with ${failedCount} failed item(s). ${resultCount} item(s) processed successfully.`, "warning", 5000);
        } else {
          showTemporaryToast(`Translation completed successfully. ${resultCount} item(s) processed.`, "success", 3000);
        }
      }).catch((error) => {
        showTemporaryToast("Translation failed: " + error.message, "error", 5000);
      });

    } catch (error: any) {
      console.error("Sidebar - Translation error:", error);
      console.error("Sidebar - Error message:", error.message);
      console.error("Sidebar - Error stack:", error.stack);
      showTemporaryToast("An error occurred while trying to submit the request. Details: " + error.message, "error", 5000);
      setShouldHideButton(false);
      setShouldHideSpinner(true);
      setProgressMessage("Submitting request...");
    }
  };

  React.useEffect(() => {
    sdk.window.startAutoResizer();
  }, [sdk.window]);

  // Final layout result
  return (
    <div>
      <FormControl className="f36-margin-bottom--m">
        <FormControl.Label>Translation Service:</FormControl.Label>
        <Select
          id="translation-service"
          name="translation-service"
          value={translationService}
          onChange={(e) => setTranslationService(e.target.value)}
        >
          {TRANSLATION_SERVICE_OPTIONS.map((option) => {
            return (
              <Select.Option value={option} key={"option-" + option}>
                {option}
              </Select.Option>
            );
          })}
        </Select>
      </FormControl>
      <FormControl>
        <FormControl.Label>From:</FormControl.Label>
        <Select
          id="sourceLanguage"
          name="sourceLanguage"
          value={sourceLanguage}
          onChange={(e) => onSelectSourceLanguage(e.target.value)}
        >
          {Object.keys(sdk.locales.names).map((key) => {
            return (
              <Select.Option value={key} key={"option-" + key}>
                {sdk.locales.names[key]}
              </Select.Option>
            );
          })}
        </Select>
      </FormControl>
      <Paragraph>
        <b>To:</b>
      </Paragraph>
      {Object.keys(sdk.locales.names).map((key) => {
        return (
          <Checkbox
            key={"chk-" + key}
            id={"chk-" + key}
            name={"chk-" + key}
            isChecked={targetLanguages.includes(key)}
            onChange={(e) => onSelectTargetLanguage(e.target.checked, key)}
          >
            {sdk.locales.names[key]}
          </Checkbox>
        );
      })}
      <FormControl style={{ marginTop: "20px" }}>
        <FormControl.Label>Comments:</FormControl.Label>
        <Textarea
          id="comments"
          name="comments"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Add any comments or instructions..."
          rows={3}
        />
      </FormControl>
      <Checkbox
        id="ckbx-export-related"
        name="ckbx-export-related"
        isChecked={exportReferencedEntries}
        onChange={() => setExportReferencedEntries(!exportReferencedEntries)}
        style={{ marginTop: "5px", marginBottom: "10px", display: "block" }}
      >
        Export related items
      </Checkbox>
      <Button
        variant="primary"
        size="small"
        className={shouldHideButton ? "hidden-spinner" : ""}
        style={{ marginTop: "10px", marginBottom: "5px" }}
        onClick={() => sendForTranslation()}
        type="submit"
      >
        Send for Translation
      </Button>{" "}
      <div id="spinner" className={shouldHideSpinner ? "hidden-spinner" : ""}>
        <Paragraph style={{ paddingBottom: "5px", paddingTop: "10px" }}>
          <b>{progressMessage}</b> <Spinner />
        </Paragraph>
      </div>
    </div>
  );
};

export default Sidebar;
